'use client';

import { createContext, useCallback, useContext, type ReactNode } from 'react';

export type AlwaysAllowRule =
  | { scope: 'terminal'; pattern: string }
  | { scope: 'tool'; tool: string };

export type OnAlwaysAllowRule = (rule: AlwaysAllowRule) => void;

type AlwaysAllowSource =
  | { allowRulePattern?: string; allowToolRule?: string }
  | undefined;

export type BuildAlwaysAllowRuleHandler = (
  actionRequest: AlwaysAllowSource,
  approve: () => void,
) => (() => void) | undefined;

const TerminalApprovalRulesContext = createContext<
  OnAlwaysAllowRule | undefined
>(undefined);

export function readAlwaysAllowRule(
  actionRequest: AlwaysAllowSource,
): AlwaysAllowRule | undefined {
  if (actionRequest?.allowRulePattern) {
    return { scope: 'terminal', pattern: actionRequest.allowRulePattern };
  }

  return actionRequest?.allowToolRule
    ? { scope: 'tool', tool: actionRequest.allowToolRule }
    : undefined;
}

export const alwaysAllowRuleLabel = (rule: AlwaysAllowRule): string =>
  rule.scope === 'terminal' ? rule.pattern : rule.tool;

export function TerminalApprovalRulesProvider({
  onAlwaysAllow,
  children,
}: {
  onAlwaysAllow?: OnAlwaysAllowRule;
  children: ReactNode;
}) {
  return (
    <TerminalApprovalRulesContext.Provider value={onAlwaysAllow}>
      {children}
    </TerminalApprovalRulesContext.Provider>
  );
}

export function useAlwaysAllowRule(): OnAlwaysAllowRule | undefined {
  return useContext(TerminalApprovalRulesContext);
}

export function useAlwaysAllowRuleHandler(): BuildAlwaysAllowRuleHandler {
  const onAlwaysAllow = useAlwaysAllowRule();

  return useCallback<BuildAlwaysAllowRuleHandler>(
    (actionRequest, approve) => {
      const rule = readAlwaysAllowRule(actionRequest);

      if (!onAlwaysAllow || !rule) {
        return undefined;
      }

      return () => {
        onAlwaysAllow(rule);
        approve();
      };
    },
    [onAlwaysAllow],
  );
}
